if ( typeof CSSSlider !== 'function' ) {
  class CSSSlider extends HTMLElement {

    constructor(){

      super();

      this._touchScreen = document.body.classList.contains('touchevents');
      this._rtl = document.documentElement.getAttribute('dir') == 'rtl';

      // create option object, from defaults
      this.o = {
        ...{
          selector: '.css-slide', 
          snapping: true, 
          groupCells: false,
          autoHeight: false, 
          navigation: true,
          navigationDOM: `
            <span class="css-slider-button css-slider-prev">‹</span>
            <span class="css-slider-button css-slider-next">›</span>`,
          thumbnails: true,
          thumbnailsDOM: `<div class="css-slider-dot-navigation"></div>`,
          thumbnailsNoInteraction: false,
          indexNav: false,
          indexNavDOM: `<div class="css-slider-index-navigation"><span class="css-slider-current">1</span> / <span class="css-slider-total">1</span></div>`,
          watchCSS: false,
          undisplay: false,
          disableSwipe: false,
          listenScroll: false,
          observer: true,
          disableMouseDownEvent: true,
          disableTabNavigation: false,
          autoplay: 0
        }, ...JSON.parse(this.dataset.options || "{}")
      };

      if ( ! this.o.watchCSS ) {
        this.initSlider();
      } else {
        this.RESIZE_WATCHER = debounce(()=>{
          const afterContent = window.getComputedStyle(this,':after').content;
          if ( afterContent.includes('css-slide') && !this.sliderEnabled ) {
            this.initSlider();
          } else if ( !afterContent.includes('css-slide') && this.sliderEnabled )  { 
            this.destroySlider();
          }
        }, 100);
        window.addEventListener('resize', this.RESIZE_WATCHER);
        this.RESIZE_WATCHER();
      }
    }

    destroySlider(){
      this.innerHTML = `${this.originalHTML}`;
      this.classList.remove('enabled');
      this.sliderEnabled = false;
      window.removeEventListener('resize', this.RESIZE_EVENT);
      window.removeEventListener('scroll', this.SCROLL_EVENT);
      window.removeEventListener('scroll', this.SCROLL_EVENT_ANIMATION);
    }

    initSlider(){

      this._readyEvent = new CustomEvent('ready');
      this._changeEvent = new CustomEvent('change');

      // create slider structure
      this.classList.add('css-slider');
      this.originalHTML = this.innerHTML;
      this.innerHTML = `<div class="css-slider-viewport">
        <div class="css-slider-holder" ${this.o.disableTabNavigation ? 'tabindex="-1"' : ''}>
          <div class="css-slider-container">
            ${this.originalHTML}
          </div>
        </div>
      </div>`;

      // add css-slide if not set
      if ( this.o.selector != '.css-slide' ) {
        this.querySelectorAll(`${this.o.selector}`).forEach((elm)=> elm.classList.add('css-slide'));
      }

      this.element = this.querySelector('.css-slider-holder');
      this.items = this.querySelectorAll(`${this.o.selector}`);
      this.indexedItems = [];
      this.index = 0;
      this.length = this.items.length;

      // Navigation
      if ( this.o.navigation || this.o.thumbnails ) {
        let container = document.createElement('div');
        container.classList.add('css-slider-navigation-container');
        container.innerHTML = `
          ${(this.o.navigation ? this.o.navigationDOM : '')}
          ${(this.o.thumbnails ? this.o.thumbnailsDOM : '')}
          ${(this.o.indexNav ? this.o.indexNavDOM : '')}`;
        
        if ( this.o.navigation ) {
          this.prevEl = container.querySelector('.css-slider-prev');
          this.nextEl = container.querySelector('.css-slider-next');
          this.prevEl.addEventListener('click', ()=> this.changeSlide('prev'));
          this.nextEl.addEventListener('click', ()=> this.changeSlide('next'));
        }
        if ( this.o.thumbnails ) {
          this.thumbnailsEl = container.querySelector('.css-slider-dot-navigation'); 
        }
        this.append(container);
      }

      if ( this.length > 1 ) {
        this.OBSERVER = new IntersectionObserver(entries=>{
          entries.forEach(entry=>{
            if ( entry.intersectionRatio >= .5 ) {
              this.index = parseInt(entry.target.getAttribute('data-index'));
              this.checkSlide();
              this.dispatchEvent(this._changeEvent);
            }
          })
        }, { threshold: [0, .5] });
      }

      this.resetSlider(true);    

      this.classList.add('enabled');
      this.sliderEnabled = true;
      this.dispatchEvent(this._readyEvent);

      if ( parseInt(this.o.autoplay) > 0 ) {
        this._initAutoplay();
      }
    }

    changeSlide(direction, behavior='smooth'){
      if ( direction == 'next' && this.index+1 < this.length ) this.index++;
      else if ( direction == 'prev' && this.index-1 >= 0 ) this.index--;
      else if ( parseInt(direction) >= 0 ) this.index = parseInt(direction);

      this.checkSlide();
      this.element.scrollTo({
        top: 0,
        left: this.indexedItems[this.index].offsetLeft,
        behavior: behavior
      });
      this.dispatchEvent(this._changeEvent);
    }

    checkSlide(){
      if ( this.o.navigation ) {
        this.prevEl.classList.toggle('disabled', this.index === 0);
        this.nextEl.classList.toggle('disabled', this.index === this.length - 1);
      }
      this.indexedItems.forEach((elm,i)=>{
        elm.classList.remove('css-slide-active','css-slide-prev','css-slide-next');
        if ( i === this.index ) elm.classList.add('css-slide-active');
        if ( i === this.index - 1 ) elm.classList.add('css-slide-prev');
        if ( i === this.index + 1 ) elm.classList.add('css-slide-next');
      });
    }

    resetSlider(nojump=false,resetIndex=true){
      this.indexedItems = this.querySelectorAll(`${this.o.selector}`);
      this.indexedItems.forEach((elm,i)=>{
        elm.setAttribute('data-index', i);
      });
      if ( resetIndex ) this.index = 0;
      this.length = this.items.length;

      if ( this.o.thumbnails ) {
        this.thumbnailsEl.innerHTML = '';
        for ( let i = 0; i < this.length; i++ ) {
          let dot = document.createElement('span');
          dot.classList.add('css-slider-dot');
          dot.dataset.index = i;
          this.thumbnailsEl.appendChild(dot);
          dot.addEventListener('click', ()=> this.changeSlide(i));
        }
        this.thumbnails = this.thumbnailsEl.querySelectorAll('.css-slider-dot');
      }

      this.checkSlide();

      if ( ! nojump ) {
        this.element.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    }

    _initAutoplay(){
      if ( this._autoplayInterval ) clearInterval(this._autoplayInterval);
      this._autoplayInterval = setInterval(()=>{
        if ( this.index + 1 == this.length ) this.changeSlide(0);
        else this.changeSlide('next');
      }, parseInt(this.o.autoplay));
    }
  }

  if ( typeof customElements.get('css-slider') == 'undefined' ) {
    customElements.define('css-slider', CSSSlider);
  }
}
